import React, { useMemo } from "react";
import { useQueryClient } from "react-query";

import { apiConfig } from "Services/config";
import { createServices } from "Services";
import { createContext, useContext } from "react";

import { MESSAGES, depoNotificationErrorConf } from "Helpers/ats.constants";
import { useNavigate } from "react-router-dom";
import { Paths } from "Router/Paths";
import { useNotify } from "./NotificationContext";
import { useUserContext } from "./UserContext";
import { ApiService } from "Services/ApiService";

export const servicesContext = createContext(undefined);

export function useServices() {
  return useContext(servicesContext);
}

export function useApi() {
  return useServices().apiService;
}

function useApiMiddleware() {
  const { setLoggedIn, setuserDetails, setServerDown } = useUserContext();
  const queryClient = useQueryClient();
  const { depoNotify } = useNotify();
  const navigate = useNavigate();

  const apiServiceClass = new ApiService(`${apiConfig.backendUrl}`);

  return useMemo(
    () => ({
      refreshToken: async () => {
        try {
          return await apiServiceClass.updateAccessToken();
        } catch (error) {
          console.log(error);
          return false;
        }
      },
      onError: async (error) => {
        try {
          if (error && error.status) {
            // A failed credential check is a form error, not an expired
            // session. Bail out before the 401 branch below, which would
            // log the operator out and redirect them mid-toast and make a
            // mistyped password look like a system failure. The Login page's
            // own onError owns the message, so we deliberately do not toast
            // here — that would show the same error twice.
            if (error.isAuthAttempt) {
              return;
            }
            if (
              error.status === 404 ||
              error.status === 500 ||
              error.status === 422 ||
              error.status === 409
            ) {
              let errors = error.errors?.length > 0 ? error.errors : null;
              if (errors) {
                depoNotify(errors[0].message || error.message, depoNotificationErrorConf);
              } else if (error?.message) {
                depoNotify(error.message, depoNotificationErrorConf);
              }
            } else if (error.status === 403) {
              // Permission denied — surface in DevTools but don't
              // toast. These come from background polling queries
              // (sidebar alerts, header counters) where the operator
              // has no recourse, and the repeating toast becomes
              // noise. Individual query handlers can still react if
              // they need to.
              // eslint-disable-next-line no-console
              console.warn("[API 403]", error.message || error);
            } else if (error.status === 401 || error.status === 429) {
              if (localStorage.getItem("user_session")) {
                await apiServiceClass.getLogoutUser();
              }
              localStorage.clear();
              // Wipe cached query data on auto-logout (session expiry /
              // 401 / idle) too — same reason as the manual logout:
              // keep the next session from inheriting stale list data
              // + search state.
              queryClient.clear();
              setLoggedIn(false);
              setuserDetails(null);
              navigate(Paths.base);
            }
          } else if (error) {
            if (error.name === "TypeError" && error.message === "Failed to fetch") {
              setServerDown(true);
            }
            depoNotify(MESSAGES.API_ERROR, depoNotificationErrorConf);
          }
        } catch (error) {
          console.log(error);
        }
      }
    }),
    [queryClient]
  );
}

export const ServicesProvider = ({ children }) => {
  const middleware = useApiMiddleware();
  const services = useMemo(() => createServices(apiConfig, undefined, middleware), [middleware]);

  return <servicesContext.Provider value={services}>{children}</servicesContext.Provider>;
};
